import { useCallback, useEffect, useState } from "react";

import {
    addBranchAccount,
    deleteBranchAccount,
    getBranchAccounts,
    updateBranchAccountPhoneNumber,
} from "services/organizationBranches/rest";
import { BranchAccountDto } from "services/organizationBranches/restModels";
import { UseNotificationSnackbarReturn } from "shared/ui/NotificationSnackbar/useNotificationSnackbar";

export interface AccountPhoneEditing {
    account: BranchAccountDto | null;
}

export const useBranchAccounts = (
    branchId: number,
    notify: UseNotificationSnackbarReturn,
    // Партнёру аккаунты заведения закрыты: запрос вернул бы только 403.
    enabled: boolean
) => {
    const [accounts, setAccounts] = useState<BranchAccountDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editing, setEditing] = useState<AccountPhoneEditing | null>(null);
    const [deleting, setDeleting] = useState<BranchAccountDto | null>(null);

    const loadAccounts = useCallback(async () => {
        setLoading(true);

        const response = await getBranchAccounts(branchId);

        setAccounts(response || []);
        setError(response === null);
        setLoading(false);
    }, [branchId]);

    useEffect(() => {
        if (enabled) loadAccounts();
    }, [enabled, loadAccounts]);

    const save = async (
        action: () => Promise<BranchAccountDto[]>,
        successTitle: string,
        failureTitle: string
    ) => {
        setSaving(true);

        try {
            setAccounts(await action());
            notify.showSuccess(successTitle);
            return true;
        } catch (caught) {
            notify.showError(failureTitle, caught);
            return false;
        } finally {
            setSaving(false);
        }
    };

    const submitPhoneNumber = async (phoneNumber: string) => {
        if (!editing) return;

        const { account } = editing;

        const saved = account
            ? await save(
                  () => updateBranchAccountPhoneNumber(branchId, account.id, { phoneNumber }),
                  "Номер телефона изменён",
                  "Не удалось изменить номер телефона"
              )
            : await save(
                  () => addBranchAccount(branchId, { phoneNumber }),
                  "Аккаунт добавлен",
                  "Не удалось добавить аккаунт"
              );

        if (saved) setEditing(null);
    };

    const confirmDeleting = async () => {
        if (!deleting) return;

        const saved = await save(
            () => deleteBranchAccount(branchId, deleting.id),
            "Аккаунт удалён",
            "Не удалось удалить аккаунт"
        );

        if (saved) setDeleting(null);
    };

    return {
        accounts,
        loading,
        error,
        saving,
        editing,
        deleting,
        onStartAdding: () => setEditing({ account: null }),
        onStartEditing: (account: BranchAccountDto) => setEditing({ account }),
        onCancelEditing: () => setEditing(null),
        onSubmitPhoneNumber: submitPhoneNumber,
        onStartDeleting: setDeleting,
        onCancelDeleting: () => setDeleting(null),
        onConfirmDeleting: confirmDeleting,
        onRetry: loadAccounts,
    };
};
